import React, { useEffect, useState } from "react";
import {
  getFurnishImageHistory,
  getDeclutteredImageHistory,
} from "../api/businesses";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";

interface ImageHistoryItem {
  id: string;
  afterUrl: string;
  updatedAt: string;
  roomType?: string; // Optional for "Remove Furniture"
  furnitureStyle?: string; // Optional for "Remove Furniture"
  variations?: Array<{
    order: number;
    url: string;
    createdAt: string;
  }>;
}

const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<ImageHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortOption, setSortOption] = useState("latest");
  const [filteredHistory, setFilteredHistory] = useState<ImageHistoryItem[]>(
    []
  );
  const [filter, setFilter] = useState({ roomType: "", furnitureStyle: "" });
  const [viewType, setViewType] = useState("furnish"); // Default to "furnish"
  const navigate = useNavigate();

  const itemsPerPage = 10;

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        if (viewType === "furnish") {
          // Fetch history for Furnish Home
          const response = await getFurnishImageHistory();
          const data = response.data.data;
          setHistory(data);
        } else if (viewType === "remove") {
          // Fetch history for Remove Furniture
          const response = await getDeclutteredImageHistory();
          const data = response.data.data;
          setHistory(data);
        }
      } catch (error) {
        console.error("Error fetching history:", error);
        alert("Failed to fetch history data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [viewType]);

  useEffect(() => {
    // Apply sorting and filtering
    const sorted = [...history].sort((a, b) => {
      return sortOption === "latest"
        ? new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        : new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    });

    const filtered = sorted.filter((item) => {
      if (viewType === "furnish") {
        const matchesRoomType = filter.roomType
          ? item.roomType === filter.roomType
          : true;
        const matchesFurnitureStyle = filter.furnitureStyle
          ? item.furnitureStyle === filter.furnitureStyle
          : true;
        return matchesRoomType && matchesFurnitureStyle;
      }
      return true; // No filters for "remove" type
    });

    setFilteredHistory(filtered);
  }, [history, sortOption, filter, viewType]);

  const handleDownload = (url: string) => {
    if (!url) {
      alert("Image URL is not available for download.");
      return;
    }
    saveAs(url, "GeneratedImage.jpg");
  };

  const handleView = (item: ImageHistoryItem) => {
    if (
      viewType === "furnish" &&
      item.variations &&
      item.variations?.length > 0
    ) {
      // Navigate to variations viewer with nested route
      navigate(`/history/variations/${item.id}`);
    } else {
      // Download single image
      handleDownload(item.afterUrl);
    }
  };

  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  const displayedItems = filteredHistory.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex-grow overflow-auto">
      <div className="p-4">
        <h2 className="text-xl md:text-3xl font-bold mb-4 text-center">
          History
        </h2>

        {/* Toggle View */}
        <div className="mb-4 flex justify-center space-x-4">
          <button
            onClick={() => setViewType("furnish")}
            className={`px-3 py-2 rounded ${
              viewType === "furnish"
                ? "bg-blue-500 text-white"
                : "bg-gray-300 text-gray-700 hover:bg-gray-400"
            }`}
          >
            Furnish Home
          </button>
          <button
            onClick={() => setViewType("remove")}
            className={`px-3 py-2 rounded ${
              viewType === "remove"
                ? "bg-blue-500 text-white"
                : "bg-gray-300 text-gray-700 hover:bg-gray-400"
            }`}
          >
            Remove Furniture
          </button>
        </div>

        {/* Sort and Filter Options */}
        <div className="mb-4 flex flex-wrap justify-between items-center">
          <div className="flex items-center space-x-2">
            <label className="text-sm">Sort by:</label>
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="border border-gray-300 rounded p-2 text-sm"
            >
              <option value="latest">Latest</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>

          {viewType === "furnish" && (
            <div className="flex flex-wrap items-center space-x-4">
              <div className="flex items-center">
                <label className="text-sm">Room Type:</label>
                <select
                  value={filter.roomType}
                  onChange={(e) =>
                    setFilter((prev) => ({
                      ...prev,
                      roomType: e.target.value,
                    }))
                  }
                  className="border border-gray-300 rounded p-2 text-sm"
                >
                  <option value="">All</option>
                  <option value="living">Living Room</option>
                  <option value="bed">Bedroom</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="dining">Dining Room</option>
                  <option value="home_office">Office</option>
                  <option value="outdoor">Outdoor</option>
                  <option value="kids_room">Kids Room</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="text-sm">Furniture Style:</label>
                <select
                  value={filter.furnitureStyle}
                  onChange={(e) =>
                    setFilter((prev) => ({
                      ...prev,
                      furnitureStyle: e.target.value,
                    }))
                  }
                  className="border border-gray-300 rounded p-2 text-sm"
                >
                  <option value="">All</option>
                  <option value="modern">Modern</option>
                  <option value="scandinavian">Scandinavian</option>
                  <option value="industrial">Industrial</option>
                  <option value="midcentury">Midcentury</option>
                  <option value="luxury">Luxury</option>
                  <option value="farmhouse">Farmhouse</option>
                  <option value="coastal">Coastal</option>
                  <option value="standard">Standard</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Image Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {displayedItems.map((item) => {
            const variationCount = item.variations?.length || 0;
            return (
              <div
                key={item.id}
                className="bg-white shadow rounded-lg overflow-hidden"
              >
                <img
                  src={item.afterUrl}
                  alt={`Generated on ${item.updatedAt}`}
                  className="w-full h-40 object-cover"
                />
                <div className="p-2 text-center">
                  <p className="text-xs text-gray-600">Generated on:</p>
                  <p className="text-xs font-semibold">
                    {new Date(item.updatedAt).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleView(item)}
                    className="mt-2 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                  >
                    {viewType === "furnish" && variationCount > 0
                      ? `View (${variationCount})`
                      : "Download"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className={`px-4 py-2 rounded ${
              page === 1
                ? "bg-gray-300"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded ${
              page === totalPages
                ? "bg-gray-300"
                : "bg-blue-500 text-white hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoryPage;
